/**
 * Base service class
 * Foundation for all API services
 * Handles authentication tokens (JLoo and Studio tokens)
 */

/** Base service constructor options */
export interface BaseServiceOptions {
  baseUrl?: string
}

/**
 * Base API service class
 * All service classes extend this to inherit token management
 */
export default class BaseService {
  protected baseUrl: string
  protected JLoToken?: string
  protected studioToken?: string

  /**
   * Create base service instance
   * @param options - Service configuration options
   */
  constructor({ baseUrl = '' }: BaseServiceOptions = {}) {
    this.baseUrl = baseUrl
  }

  /**
   * Set JLoo authentication token
   * @param value - JLoo token (without 'Bearer' prefix)
   */
  setJLoToken(value = ''): void {
    this.JLoToken = `Bearer ${value}`
  }

  /**
   * Set Studio authentication token
   * @param value - Studio token (without 'Bearer' prefix)
   */
  setStudioToken(value = ''): void {
    this.studioToken = `Bearer ${value}`
  }

  getTrafficAttributionHeaders(): Record<string, string> {
    try {
      const trafficSource = window.sessionStorage?.getItem?.('traffic_source')?.trim()
      return trafficSource ? { 'X-Studio-Traffic-Source': trafficSource } : {}
    } catch {
      return {}
    }
  }

  protected studioHeaders(): Record<string, string> {
    if (!this.studioToken) throw new Error('studioToken is not set')
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Studio-Authorization': this.studioToken,
    }
  }

  protected studioHeadersWithAttribution(): Record<string, string> {
    return {
      ...this.studioHeaders(),
      ...this.getTrafficAttributionHeaders(),
    }
  }

  protected jloHeaders(): Record<string, string> {
    if (!this.JLoToken) throw new Error('JLoToken is not set')
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: this.JLoToken,
    }
  }

  protected graphqlHeaders(locale: string, clientName: string): Record<string, string> {
    if (!this.studioToken) throw new Error('studioToken is not set')
    return {
      Accept: 'application/json',
      'X-Studio-Authorization': this.studioToken,
      'x-trv-language': locale,
      'Apollographql-Client-Name': clientName,
      'Apollographql-Client-Version': '1.0.0',
    }
  }
}